import { gql } from '@apollo/client';

/**
 * List of mirrored Fakturownia invoices for the Invoices page.
 * All filter args are passed straight through to the backend resolver —
 * the server has no text-search/`overdueOnly` knobs yet, so those are
 * handled client-side over the current page (TZ §3.2, §4.2).
 */
// Ids-only projection of the same feed — for counters that only need «how
// many rows match» (the dashboard reconcile KPI walked full rows before —
// spec 0008 round 2). The proper long-term fix is a needsLinkOnly arg on
// fakturowniaInvoiceAggregates (backend follow-up).
export const FakturowniaInvoiceIdsQuery = gql`
  query FakturowniaInvoiceIds(
    $direction: String
    $needsLinkOnly: Boolean
    $page: Int
    $perPage: Int
  ) {
    fakturowniaInvoices(
      direction: $direction
      needsLinkOnly: $needsLinkOnly
      page: $page
      perPage: $perPage
    ) {
      id
    }
  }
`;

export const FakturowniaInvoicesQuery = gql`
  query FakturowniaInvoices(
    $direction: String
    $status: String
    $matchStatus: String
    $kind: String
    $studentId: ID
    $groupId: ID
    $agreementId: ID
    $dateFrom: Date
    $dateTo: Date
    $page: Int
    $perPage: Int
    $search: String
    $overdueOnly: Boolean
    $parentId: ID
    $needsLinkOnly: Boolean
    $provisionBlockedOnly: Boolean
  ) {
    fakturowniaInvoices(
      direction: $direction
      status: $status
      matchStatus: $matchStatus
      kind: $kind
      studentId: $studentId
      groupId: $groupId
      agreementId: $agreementId
      dateFrom: $dateFrom
      dateTo: $dateTo
      page: $page
      perPage: $perPage
      search: $search
      overdueOnly: $overdueOnly
      parentId: $parentId
      needsLinkOnly: $needsLinkOnly
      provisionBlockedOnly: $provisionBlockedOnly
    ) {
      id
      pk
      number
      direction
      kind
      status
      issueDate
      paymentTo
      currency
      priceGross
      paid
      outstanding
      buyerEmail
      buyerName
      buyerTaxNo
      matchStatus
      groupMatchSource
      linkedStudent {
        id
        user {
          id
          email
          firstName
          lastName
        }
      }
      linkedGroup {
        id
        name
        course {
          id
          title
        }
      }
      payment {
        id
        date
        amountAgreement
        currency
        agreement {
          id
        }
      }
      contribution {
        id
        date
        amountForeign
        status
      }
      contributionMatchSource
      provisionBlockedReason
      agreement {
        id
        agreementTitle
        student {
          id
          user {
            id
            firstName
            lastName
            email
          }
        }
        group {
          id
          name
        }
      }
      isCorrection
      isAdvance
      isFinal
      isEstimate
      isQuotation
      parentInvoice {
        id
        number
      }
      corrections {
        id
        outstanding
      }
    }
  }
`;

/** Single Fakturownia invoice — detail screen (TZ §3.3). */
export const FakturowniaInvoiceQuery = gql`
  query FakturowniaInvoice($id: ID!) {
    fakturowniaInvoice(id: $id) {
      id
      pk
      fakturowniaId
      number
      kind
      direction
      status
      issueDate
      sellDate
      paymentTo
      currency
      priceNet
      priceGross
      paid
      outstanding
      buyerEmail
      buyerName
      buyerTaxNo
      sellerName
      correctionReason
      accountingKind
      matchStatus
      groupMatchSource
      linkedAt
      linkedStudent {
        id
        user {
          id
          email
          firstName
          lastName
          phone
        }
      }
      linkedGroup {
        id
        name
        course {
          id
          title
        }
      }
      payment {
        id
        date
        amountAgreement
        currency
        agreement {
          id
          agreementTitle
          student {
            id
            user {
              id
              firstName
              lastName
              email
              phone
            }
          }
          group {
            id
            name
            course {
              id
              title
            }
          }
        }
      }
      agreement {
        id
        agreementTitle
        student {
          id
          user {
            id
            firstName
            lastName
            email
            phone
          }
        }
        group {
          id
          name
          course {
            id
            title
          }
        }
      }
      fakturowniaPayments {
        id
        pk
        fakturowniaId
        paidDate
        price
        currency
        kind
      }
      isCorrection
      isAdvance
      isFinal
      isEstimate
      isQuotation
      parentInvoice {
        id
        number
        issueDate
        priceGross
        status
        currency
      }
      corrections {
        id
        pk
        number
        issueDate
        priceGross
        currency
        correctionReason
        status
        outstanding
      }
      advances {
        id
        pk
        number
        issueDate
        priceGross
        currency
        status
        paid
      }
      finalInvoice {
        id
        pk
        number
        issueDate
        priceGross
        currency
        status
        paid
      }
      rawPayload
    }
  }
`;

/** Live sync status — widget in invoices header + polling target (TZ §3.1). */
export const FakturowniaSyncStatusQuery = gql`
  query FakturowniaSyncStatus {
    fakturowniaSyncStatus {
      isRunning
      lastRun {
        startedAt
        finishedAt
        status
        stats
        errorMessage
        taskName
      }
      lastSuccess {
        startedAt
        stats
      }
    }
    fakturowniaUnmatchedCount
  }
`;

/**
 * Agreements + their contribution schedule for the LinkInvoicePaymentPopup.
 * ТЗ 0004: the popup picks a student, then one of their agreements, then an
 * unpaid взнос to pin the invoice to (`linkInvoiceToContribution`). The
 * legacy payment-level linkage was removed — Payment is a hidden technical
 * record now, so the query no longer selects `payments`.
 */
export const StudentAgreementsForLinkingQuery = gql`
  query StudentAgreementsForLinking($studentId: ID!) {
    student(id: $studentId) {
      id
      user {
        id
        firstName
        lastName
        email
      }
      agreements {
        id
        agreementTitle
        currency
        terminated
        group {
          id
          name
        }
        contributions(orderByDateFilter: true) {
          edges {
            node {
              id
              date
              amountForeign
              amountByn
              status
              debt
              complete
              remainder
            }
          }
        }
      }
    }
  }
`;

/** Standalone count for the unmatched badge — cheap, separate cache key. */
export const FakturowniaUnmatchedCountQuery = gql`
  query FakturowniaUnmatchedCount {
    fakturowniaUnmatchedCount
  }
`;

/** Expenses register — filters mirror TZ §3.5 1:1. */
export const ExpensesQuery = gql`
  query Expenses(
    $dateFrom: Date
    $dateTo: Date
    $source: String
    $categorySlug: String
    $recurrence: String
    $status: String
    $teacherId: ID
    $groupId: ID
    $legalEntityId: ID
    $page: Int
    $perPage: Int
  ) {
    expenses(
      dateFrom: $dateFrom
      dateTo: $dateTo
      source: $source
      categorySlug: $categorySlug
      recurrence: $recurrence
      status: $status
      teacherId: $teacherId
      groupId: $groupId
      legalEntityId: $legalEntityId
      page: $page
      perPage: $perPage
    ) {
      id
      pk
      date
      amount
      amountPln
      currency
      description
      counterpartyName
      counterpartyTaxNo
      source
      paidAt
      documentUrl
      notes
      recurrence
      recurrenceDay
      recurrenceUntil
      status
      isCorrection
      correctionReason
      category {
        id
        name
        slug
      }
      relatedTeacher {
        id
        user {
          id
          firstName
          lastName
        }
      }
      relatedGroup {
        id
        name
      }
      relatedFakturowniaInvoice {
        id
        pk
        number
        kind
        correctionReason
        parentInvoice {
          id
          pk
          number
        }
      }
    }
  }
`;

/** Single expense — detail/edit screen (TZ §3.7). */
export const ExpenseQuery = gql`
  query Expense($id: ID!) {
    expense(id: $id) {
      id
      pk
      date
      amount
      amountPln
      currency
      description
      counterpartyName
      counterpartyTaxNo
      source
      paidAt
      documentUrl
      notes
      recurrence
      recurrenceDay
      recurrenceUntil
      status
      isCorrection
      correctionReason
      category {
        id
        name
        slug
      }
      relatedTeacher {
        id
        user {
          id
          firstName
          lastName
        }
      }
      relatedGroup {
        id
        name
        course {
          id
          title
        }
      }
      relatedFakturowniaInvoice {
        id
        pk
        number
        kind
        status
        correctionReason
        parentInvoice {
          id
          pk
          number
        }
      }
    }
  }
`;

/**
 * Received (`direction='received'`) Fakturownia invoices eligible to back an
 * expense — picker source for ExpenseFormPopup. `unlinkedOnly` (default true)
 * hides invoices already pinned to an Expense; quotation kinds are excluded
 * server-side. Selecting one substitutes amount/currency/date/counterparty.
 */
export const ReceivedInvoicesForExpenseQuery = gql`
  query ReceivedInvoicesForExpense(
    $dateFrom: Date
    $dateTo: Date
    $unlinkedOnly: Boolean
  ) {
    receivedInvoicesForExpense(
      dateFrom: $dateFrom
      dateTo: $dateTo
      unlinkedOnly: $unlinkedOnly
    ) {
      pk
      number
      issueDate
      paymentTo
      currency
      priceGross
      buyerName
      buyerTaxNo
      accountingKind
      kind
      status
    }
  }
`;

/** Categories dropdown source + quick-create target. */
export const ExpenseCategoriesQuery = gql`
  query ExpenseCategories {
    expenseCategories {
      id
      pk
      name
      slug
      isActive
    }
  }
`;

/** Aggregates for the mini-dashboard on Expenses (TZ §3.8, Phase 2). */
export const ExpenseAggregatesQuery = gql`
  query ExpenseAggregates(
    $dateFrom: Date!
    $dateTo: Date!
    $groupBy: String!
    $teacherId: ID
  ) {
    expenseAggregates(
      dateFrom: $dateFrom
      dateTo: $dateTo
      groupBy: $groupBy
      teacherId: $teacherId
    ) {
      group
      total
      count
    }
  }
`;

/**
 * Server-side invoice aggregates (Phase 3, backend TZ §2). Replaces the
 * per_page=500 + client sum() pattern: Dashboard KPI and Group/Student
 * detail tabs use this for revenue/outstanding in native (PLN) units.
 * Phase 5 dropped `*Byn` fields — schema is currency-neutral now.
 */
export const FakturowniaInvoiceAggregatesQuery = gql`
  query FakturowniaInvoiceAggregates(
    $direction: String
    $studentId: ID
    $groupId: ID
    $dateFrom: Date
    $dateTo: Date
    $groupBy: String!
  ) {
    fakturowniaInvoiceAggregates(
      direction: $direction
      studentId: $studentId
      groupId: $groupId
      dateFrom: $dateFrom
      dateTo: $dateTo
      groupBy: $groupBy
    ) {
      group
      totalGross
      totalPaid
      totalOutstanding
      count
    }
  }
`;

/**
 * Preflight for «Выставить фактуру» (real Fakturownia VAT document).
 *
 * Operation name deliberately differs from the backend field
 * (`fakturowniaContributionBillability`) — an operation named after the field
 * collapses the generated result type into the schema payload type.
 *
 * This is the SAME `check_billability()` the mutation runs, so the popup
 * shows exactly what the server is about to POST — buyer identity included.
 * Never confirm an invoice against cached agreement fields: a stale cache
 * would display a buyer different from the one printed on the document.
 * Omit `contributionId` to preview the server's own «ближайший взнос» pick.
 */
export const InvoiceIssuePreflightQuery = gql`
  query InvoiceIssuePreflight($agreementId: ID!, $contributionId: ID) {
    fakturowniaContributionBillability(
      agreementId: $agreementId
      contributionId: $contributionId
    ) {
      billable
      reason
      agreementId
      contributionId
      studentName
      groupName
      courseName
      amount
      currency
      paymentTo
      positionName
      taxRate
      buyerName
      buyerEmail
      buyerTaxNo
      buyerAddress
      missingFields
    }
  }
`;

/**
 * Per-agreement preflight over a WHOLE group — the source for the bulk-issue
 * drawer's picker (`fakturowniaGroupBillingPreview`). Each row is the SAME
 * `FakturowniaBillabilityType` the single-student preflight returns, so the
 * per-row badges reuse `core/utils/invoiceIssueReasons` unchanged.
 *
 * Operation name deliberately differs from the backend field — an operation
 * named after the field collapses the generated result type into the schema
 * payload type (see the note on `InvoiceIssuePreflight`).
 *
 * Non-billable rows are RETURNED (never filtered), because «пропущено» has to
 * be visible in the drawer or the operator believes the whole group was billed.
 */
export const GroupInvoiceIssuePreflightQuery = gql`
  query GroupInvoiceIssuePreflight($groupId: ID!) {
    fakturowniaGroupBillingPreview(groupId: $groupId) {
      total
      billableCount
      skippedCount
      rows {
        billable
        reason
        agreementId
        contributionId
        studentName
        groupName
        courseName
        amount
        currency
        paymentTo
        positionName
        taxRate
        buyerName
        buyerEmail
        buyerTaxNo
        buyerAddress
        missingFields
      }
    }
  }
`;

/**
 * Live progress of one bulk-issue run — the drawer's poll target
 * (`fakturowniaBillingJob`). Polled at 1500ms until the job reaches a terminal
 * status (`success | partial | failed`); the per-row `status`
 * (`pending | issued | skipped | failed`) drives the result table.
 *
 * `id` is queried alongside `pk` and is the value polled with: the backend
 * `fakturowniaBillingJob(id:)` runs it through `to_pk`, which accepts both the
 * relay global id and a raw pk. Operation name differs from the field for the
 * result-type-collapse reason above.
 */
export const InvoiceIssueJobProgressQuery = gql`
  query InvoiceIssueJobProgress($id: ID!) {
    fakturowniaBillingJob(id: $id) {
      id
      pk
      status
      total
      issuedCount
      skippedCount
      failedCount
      rows {
        pk
        status
        reason
        amount
        currency
        studentName
        invoiceNumber
        viewUrl
      }
    }
  }
`;
